import { colours } from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { headerSignInStyles } from "@affinity/Overrides/Widgets/Common/HeaderSignIn";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    topNavigation?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    header?: GridContainerProps;
    logo?: GridItemProps;
    nav?: GridItemProps;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [false, false, false, false, false],
        css: css`
            position: relative;
            z-index: ${styleHelpers.topZindex};
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    topNavigation: {
        css: css`
            display: none;
            width: 100%;
            color: ${colours.white};
            a {
                color: ${colours.white};
                text-decoration: none;
            }
            a: hover {
                color: ${colours.lightBlue};
                text-decoration: underline;
            }
            div {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 15px;
            }
            @media (min-width: ${styleHelpers.rem(1024)}) {
                display: block;
            }
        `,
    },
    header: {
        css: css`
            position: relative;
            margin: 0 auto;
            padding: ${styleHelpers.rem(10)} ${styleHelpers.rem(20)};
            z-index: 1;
            div {
                margin: 0;
            }
            [class*="GridOffset"] {
                width: 100%;
                display: flex;
                align-items: end;
            }
            @media (min-width: ${styleHelpers.breakpointWidth("md")}) {
                padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(40)};
            }
            @media (min-width: ${styleHelpers.breakpointWidth("lg")}) {
                padding: ${styleHelpers.rem(20)} ${styleHelpers.rem(60)};
            }
            ::after {
                position: absolute;
                top: 0;
                left: 0;
                margin: 0 calc((100% - 100vw) / 2);
                width: 100vw;
                height: 100%;
                z-index: -1;
                display: block;
                content: "";
                background-color: ${colours.darkBlue};
            }
        `,
    },
    logo: {
        width: [4, 4, 2, 2, 2],
        css: css`
            padding: 0;
            display: flex;
            align-items: center;
            align-self: center;
        `,
    },
    nav: {
        width: [8, 8, 10, 10, 10],
        css: css`
            display: flex;
            padding: 0;
            justify-content: flex-end;
            align-items: center;
            gap: 15px;
            @media (min-width: ${styleHelpers.breakpointWidth("xl")}) {
                gap: 50px;
            }
            div {
                width: auto;
            }
            ${styleHelpers.reverseMobile}
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page as="header" {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <GridContainer {...styles.header}>
                    <StyledWrapper {...styles.topNavigation}>
                        <Zone contentId={id} zoneName="TopNavigation" />
                    </StyledWrapper>
                    <EagerLoadingLogoProvider value={true}>
                        <GridItem {...styles.logo}>
                            <Zone contentId={id} zoneName="Logo" />
                        </GridItem>
                    </EagerLoadingLogoProvider>
                    <GridItem {...styles.nav}>
                        <Zone contentId={id} zoneName="MainNavigation" />
                        <Hidden below="lg">
                            <Zone contentId={id} zoneName="SignIn" />
                        </Hidden>
                    </GridItem>
                </GridContainer>
                <Zone contentId={id} zoneName="Breadcrumb" />
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
